/**
 * lazyWithRetry — Robuster Wrapper um React.lazy() mit automatischem Retry
 * bei ChunkLoadError / dynamic import Fehlern.
 *
 * Problem: Wenn ein Deploy neue Assets deployed und ein User noch die alte
 * index.html gecached hat (oder während des Deploys navigiert), schlagen
 * dynamic imports fehl weil die alten Chunk-Hashes nicht mehr existieren.
 *
 * Lösung:
 *   1. Erster Fehler → 1s warten, Retry (Netzwerkglitch)
 *   2. Zweiter Fehler → Cache-busted Retry (neuer Query-Param)
 *   3. Dritter Fehler → Kontrollierter Page-Reload (einmalig pro Session)
 *   4. Danach → Error propagieren (kein Endlos-Reload)
 *
 * DSGVO: Keine Patientendaten in Logs. Nur technische Fehler-Metadaten.
 */
import { lazy, type ComponentType } from "react";

type ModuleImport<T> = () => Promise<{ default: T }>;

const RELOAD_KEY = "mediflow_chunk_reload";
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

/**
 * Prüft ob ein Fehler ein ChunkLoadError / dynamic import Fehler ist.
 */
function isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;

    const msg = error.message.toLowerCase();
    return (
        // Chrome / Chromium
        msg.includes("failed to fetch dynamically imported module") ||
        // Firefox
        msg.includes("error loading dynamically imported module") ||
        // Webpack
        msg.includes("loading chunk") ||
        msg.includes("loading css chunk") ||
        msg.includes("chunkloaderror") ||
        // Safari-spezifisch
        msg.includes("undefined is not an object") ||
        // Netzwerk-Fehler beim Laden
        (msg.includes("failed to fetch") && error.name === "TypeError") ||
        // Generisch: Import-URL nicht erreichbar (alle Browser)
        msg.includes("importing a module script failed")
    );
}

/**
 * Wartet eine bestimmte Zeit.
 */
function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Versucht einen dynamic import mit Retry-Logik.
 */
async function importWithRetry<T>(
    importFn: ModuleImport<T>,
    retriesLeft: number = MAX_RETRIES,
): Promise<{ default: T }> {
    try {
        return await importFn();
    } catch (error) {
        if (!isChunkLoadError(error) || retriesLeft <= 0) {
            throw error;
        }

        console.warn(
            `[MediFlow] Chunk-Load fehlgeschlagen, Retry in ${RETRY_DELAY_MS}ms… (${retriesLeft} verbleibend)`,
        );

        await delay(RETRY_DELAY_MS);

        return importWithRetry(importFn, retriesLeft - 1);
    }
}

/**
 * Führt einen kontrollierten Page-Reload durch (einmalig pro Session).
 *
 * Verhindert Endlos-Reload-Loops indem ein sessionStorage Flag gesetzt wird.
 * Wenn der Flag bereits gesetzt ist, wird der Fehler stattdessen propagiert.
 */
function attemptRecoveryReload(): boolean {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
        return false;
    }

    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (alreadyReloaded) {
        // Bereits einmal neu geladen — kein erneuter Versuch
        console.error(
            "[MediFlow] Chunk-Load nach Reload immer noch fehlgeschlagen. Bitte Seite manuell neu laden.",
        );
        return false;
    }

    console.warn("[MediFlow] Chunk-Load fehlgeschlagen — einmaliger Page-Reload…");
    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
    window.location.reload();
    return true;
}

/**
 * Setzt das Reload-Flag zurück (nach erfolgreichem Laden).
 * Wird beim App-Start aufgerufen.
 */
export function clearChunkReloadFlag(): void {
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(RELOAD_KEY);
    }
}

/**
 * React.lazy() Wrapper mit automatischem Retry bei ChunkLoadError.
 *
 * @example
 * ```tsx
 * // Statt:
 * const DashboardPage = lazy(() => import("@/routes/dashboard"));
 *
 * // Verwende:
 * const DashboardPage = lazyWithRetry(() => import("@/routes/dashboard"));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(importFn: ModuleImport<T>) {
    return lazy(async () => {
        try {
            const module = await importWithRetry(importFn);
            // Import erfolgreich — Reload-Flag zurücksetzen
            clearChunkReloadFlag();
            return module;
        } catch (error) {
            // Alle Retries fehlgeschlagen — letzte Chance: Page-Reload
            if (isChunkLoadError(error)) {
                const willReload = attemptRecoveryReload();
                if (willReload) {
                    // Page wird neu geladen — Rückgabe eines "leeren" Moduls
                    // um den Error nicht zu propagieren während der Reload läuft
                    return { default: (() => null) as unknown as T };
                }
            }
            throw error;
        }
    });
}

/**
 * Returns a router v6.4 `lazy` compliant object: `{ Component: ... }`
 * Das nutzt die interne Router-Logik, löst Suspense intern auf und triggert `useNavigation().state === "loading"`.
 */
export function lazyWithRetryRoute<T extends ComponentType<unknown>>(importFn: ModuleImport<T>) {
    return async () => {
        try {
            const module = await importWithRetry(importFn);
            clearChunkReloadFlag();
            return { Component: module.default };
        } catch (error) {
            if (isChunkLoadError(error)) {
                const willReload = attemptRecoveryReload();
                if (willReload) {
                    // Promise that never resolves so we don't flash an error during reload
                    return new Promise<{ Component: T }>(() => {});
                }
            }
            throw error;
        }
    };
}
